import React, { useState, useRef, useEffect } from "react";
import { X, ChevronDown, Check, Loader2 } from "lucide-react";
import { useContactModal } from "../context/ContactContext";
import ArrowButton from "../components/ArrowButton";
import splashLogo from "../assets/logo/SplashLoader.svg";
import "./ContactUs.css";

import { getApiBaseUrl } from "../utils/apiConfig";

const API_URL = getApiBaseUrl();

const PROJECT_FOCUS_OPTIONS = [
  "Create a Brand New Website (Design + Development)",
  "Design a Website Only (Figma / UI / UX Layouts)",
  "Develop an Existing Design (Coding / Webflow / WordPress)",
  "Redesign / Overhaul an Existing Website",
  "E-Commerce Setup (Online Store / Shopify / Wix / Custom)",
  "Website Maintenance & Speed Optimization /Bugs Fixing",
  "I'm Not Sure Yet / Let's Discuss",
];

const TARGET_DATE_OPTIONS = [
  "ASAP (Within 1-2 Weeks)",
  "1 Month",
  "2 - 3 Months",
  "Flexible / Exploring Options",
];

// Custom Dropdown Component matching exact mockup screenshot
const CustomDropdown = ({ options, value, onChange, placeholder, required }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        className={`custom-dropdown-trigger ${value ? "selected" : ""} ${
          isOpen ? "open" : ""
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="dropdown-trigger-text">
          {value || placeholder}
        </span>
        <ChevronDown
          size={18}
          className={`dropdown-trigger-arrow ${isOpen ? "rotate" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="custom-dropdown-menu">
          <div className="custom-dropdown-list">
            {options.map((option, idx) => {
              const isSelected = value === option;
              return (
                <div
                  key={idx}
                  className={`custom-dropdown-item ${
                    isSelected ? "is-selected" : ""
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  <span className="dropdown-item-text">{option}</span>
                  {isSelected && <Check size={18} className="dropdown-check-icon" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const ContactUs = () => {
  const { isContactOpen, closeContactModal } = useContactModal();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    projectFocus: "",
    message: "",
    targetDate: "",
    budget: "",
    recommendBudget: false,
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (isContactOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isContactOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectChange = (fieldName, selectedValue) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: selectedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!formData.fullName.trim() || !formData.email.trim()) {
      setErrorMsg("Please fill in required fields (Name and Email).");
      return;
    }

    if (!formData.projectFocus) {
      setErrorMsg("Please select your Project Focus.");
      return;
    }

    if (!formData.targetDate) {
      setErrorMsg("Please select your Target Date.");
      return;
    }

    setLoading(true);

    try {
      const rawBudget = formData.budget ? formData.budget.trim() : "";
      const formattedBudget = formData.recommendBudget
        ? "Let Metatrybe recommend a budget"
        : rawBudget
        ? rawBudget.startsWith("$")
          ? rawBudget
          : `$${rawBudget}`
        : "Not Specified";

      const payload = {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        projectFocus: formData.projectFocus,
        message: formData.message || "No description provided.",
        targetDate: formData.targetDate,
        budget: formattedBudget,
        recommendBudget: formData.recommendBudget,
        country: "Not Specified",
        budgetRange: formattedBudget,
        projectType: formData.projectFocus || "general",
      };

      const res = await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to submit message.");
      }

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          company: "",
          projectFocus: "",
          message: "",
          targetDate: "",
          budget: "",
          recommendBudget: false,
        });
        closeContactModal();
      }, 2500);
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isContactOpen) return null;

  return (
    <div
      className="contact-modal-overlay"
      onClick={closeContactModal}
      data-lenis-prevent="true"
      data-lenis-prevent-wheel="true"
      data-lenis-prevent-touch="true"
    >
      <div
        className="contact-modal-container"
        onClick={(e) => e.stopPropagation()}
        data-lenis-prevent="true"
        data-lenis-prevent-wheel="true"
        data-lenis-prevent-touch="true"
      >
        {/* Header */}
        <div className="contact-modal-header">
          <div className="contact-header-left">
            <div className="contact-logo-box">
              <img src={splashLogo} alt="Metatrybe Logo" className="contact-logo-img" />
            </div>
            <div className="contact-header-title-box">
              <h2 className="contact-modal-title">Connect With Metatrybe</h2>
              <p className="contact-modal-subtitle">Responds in about 1-2 hour</p>
            </div>
          </div>
          <button
            type="button"
            className="contact-close-btn"
            onClick={closeContactModal}
            aria-label="Close form"
          >
            <X size={20} />
          </button>
        </div>

        <div
          className="contact-modal-body"
          data-lenis-prevent="true"
          data-lenis-prevent-wheel="true"
          data-lenis-prevent-touch="true"
        >
          {submitted ? (
            <div className="contact-success-state">
              <div className="contact-success-icon">
                <Check size={32} />
              </div>
              <h3>Thank You!</h3>
              <p>Your message has been sent successfully. We will get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              {errorMsg && <div className="contact-error-banner">{errorMsg}</div>}

              {/* Section 1: Contact Information */}
              <div className="contact-form-section">
                <div className="section-title-group">
                  <h3 className="section-title">
                    Contact Information <span className="pink-asterisk">*</span>
                  </h3>
                  <p className="section-subtitle">
                    Please provide your details so our team can get back to you promptly.
                  </p>
                </div>

                <div className="contact-grid-2">
                  <div className="form-group">
                    <label htmlFor="fullName">Full Name</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      placeholder="John Doe"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="john@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="+1 123 654 789"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">Company Name (Optional)</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      placeholder="Enter Company Name (Apple)"
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: What's Your Project Focus & Details? */}
              <div className="contact-form-section">
                <div className="section-title-group">
                  <h3 className="section-title">
                    What's Your Project Focus & Details? <span className="pink-asterisk">*</span>
                  </h3>
                  <p className="section-subtitle">
                    Select the option that best fits your current goals, or just let us know if you're unsure.
                  </p>
                </div>

                <div className="form-group">
                  <CustomDropdown
                    options={PROJECT_FOCUS_OPTIONS}
                    value={formData.projectFocus}
                    onChange={(val) => handleSelectChange("projectFocus", val)}
                    placeholder="Please Select"
                    required
                  />
                </div>

                <div className="form-group margin-top-sm">
                  <div className="label-with-hint">
                    <label htmlFor="message">Describe your project (Optional)</label>
                    <span className="field-hint">Minimum 50 characters</span>
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Include any project details, requirements, or goals..."
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                  ></textarea>
                </div>
              </div>

              {/* Section 3: Target Date & Project Budget */}
              <div className="contact-form-section">
                <div className="contact-grid-2">
                  <div className="form-group">
                    <h3 className="section-title small">
                      Target Date <span className="pink-asterisk">*</span>
                    </h3>
                    <p className="section-subtitle">
                      Select when you need the project to be completed
                    </p>
                    <CustomDropdown
                      options={TARGET_DATE_OPTIONS}
                      value={formData.targetDate}
                      onChange={(val) => handleSelectChange("targetDate", val)}
                      placeholder="Please Select"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <h3 className="section-title small">
                      Project Budget <span className="pink-asterisk">*</span>
                    </h3>
                    <p className="section-subtitle">
                      Metatrybe's minimum project rate is $2,000 (USD)
                    </p>
                    <div className="input-with-symbol">
                      <span className="currency-symbol">$</span>
                      <input
                        type="text"
                        name="budget"
                        placeholder="Enter Amount"
                        value={formData.recommendBudget ? "" : formData.budget}
                        onChange={handleChange}
                        disabled={formData.recommendBudget}
                        required={!formData.recommendBudget}
                      />
                    </div>
                  </div>
                </div>

                <div className="checkbox-group margin-top-sm">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      name="recommendBudget"
                      checked={formData.recommendBudget}
                      onChange={handleChange}
                    />
                    <span className="checkmark"></span>
                    <span className="checkbox-label-text">
                      Let Metatrybe recommend a budget
                    </span>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="contact-submit-wrapper">
                {loading ? (
                  <button type="button" className="arrow-btn loading-btn" disabled>
                    <span className="arrow-btn-text">Sending...</span>
                    <Loader2 size={18} className="animate-spin" />
                  </button>
                ) : (
                  <ArrowButton
                    text="Send Message"
                    type="submit"
                    className="contact-arrow-submit"
                  />
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
